import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store/database';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './ui/NotificationBell';
import { useState } from 'react';

export default function Layout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useStore(s => s.logout);
  const isAdmin = useStore(s => s.isAdmin);
  const [menuOpen, setMenuOpen] = useState(false);

  const links = [
    { to: '/house', label: t('nav.dashboard') },
    { to: '/releases', label: t('nav.releases') },
    { to: '/archive', label: t('nav.archive') },
    { to: '/messages', label: t('nav.messages') },
    { to: '/events', label: t('nav.events') },
    { to: '/invite', label: t('nav.invite') },
    { to: '/membership', label: t('nav.membership') },
    { to: '/account', label: t('nav.account') },
  ];

  const navCls = (isActive: boolean) =>
    `text-[11px] tracking-widest uppercase font-sans transition-colors ${isActive ? 'text-charcoal border-b border-gold pb-0.5' : 'text-charcoal/40 hover:text-charcoal/70'}`;

  return (
    <div className="min-h-dvh bg-ivory text-charcoal">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-ivory/95 backdrop-blur-sm border-b border-charcoal/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <NavLink to="/house" className="font-serif text-lg tracking-widest-xl">AH</NavLink>
          
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-5">
            {links.map(l => (
              <NavLink key={l.to} to={l.to} end={l.to === '/house'} className={({ isActive }) => navCls(isActive)}>
                {l.label}
              </NavLink>
            ))}
            {isAdmin() && (
              <NavLink to="/admin" className={({ isActive }) => navCls(isActive)}>{t('nav.admin')}</NavLink>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <NotificationBell />
            <LanguageSwitcher />
            <button onClick={() => { logout(); navigate('/login'); }} className="text-[11px] tracking-widest uppercase font-sans text-charcoal/40 hover:text-charcoal transition-colors">
              {t('nav.logout')}
            </button>
          </div>

          {/* Mobile hamburger */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2">
            <div className="space-y-1">
              <div className={`w-5 h-px bg-charcoal transition-transform ${menuOpen ? 'rotate-45 translate-y-[3px]' : ''}`} />
              <div className={`w-5 h-px bg-charcoal transition-opacity ${menuOpen ? 'opacity-0' : ''}`} />
              <div className={`w-5 h-px bg-charcoal transition-transform ${menuOpen ? '-rotate-45 -translate-y-[3px]' : ''}`} />
            </div>
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-charcoal/5 bg-ivory px-4 py-4 space-y-3">
            {links.map(l => (
              <NavLink key={l.to} to={l.to} end={l.to === '/house'} onClick={() => setMenuOpen(false)}
                className={({ isActive }) => `block ${navCls(isActive)}`}>
                {l.label}
              </NavLink>
            ))}
            {isAdmin() && (
              <NavLink to="/admin" onClick={() => setMenuOpen(false)} className={({ isActive }) => `block ${navCls(isActive)}`}>
                {t('nav.admin')}
              </NavLink>
            )}
            <div className="pt-2 border-t border-charcoal/5">
              <LanguageSwitcher />
            </div>
            <button onClick={() => { logout(); navigate('/login'); setMenuOpen(false); }}
              className="text-[11px] tracking-widest uppercase font-sans text-charcoal/40 hover:text-charcoal">
              {t('nav.logout')}
            </button>
          </div>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Outlet />
      </main>
    </div>
  );
}
